// Care Plans & ADLs — every client's plan status at a glance, with the plan
// editor in a drawer. Statuses: No plan, Draft, Active, Review due, Review
// overdue, Newer assessment (the assessment changed after the plan went live).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import useIsMobile from '../../hooks/useIsMobile';
import DataTable from '../../components/DataTable';
import { StatTile } from '../../components/StatTile';
import CarePlanEditor from '../../components/clinical/CarePlanEditor';
import { getCarePlanBoard, draftPlan } from '../../lib/carePlans';
import { isReceivingService } from '../../lib/serviceModel';

const fmtD = (d) => d ? format(new Date(`${String(d).slice(0, 10)}T12:00:00`), 'MMM d, yyyy') : '—';
const TONE = { danger: { bg: 'var(--danger-bg)', color: 'var(--danger)', border: 'var(--danger-border)' }, warn: { bg: 'var(--warn-bg)', color: 'var(--warn)', border: 'var(--warn-border)' }, success: { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-border)' } };
const FILTERS = [['all', 'All'], ['none', 'No plan'], ['draft', 'Draft'], ['due', 'Review due'], ['overdue', 'Overdue'], ['stale', 'Newer assessment'], ['active', 'Active']];

export default function CarePlans() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [drafting, setDrafting] = useState(false);

  // One draft per client that has a nurse assessment and no plan yet. The
  // RN still reviews, signs and activates each one.
  const draftAll = async () => {
    const todo = rows.filter(r => r.planStatus.key === 'none' && r.assessment);
    if (!todo.length) { setError('Every client with a nurse assessment already has a plan.'); return; }
    if (!window.confirm(`Draft care plans for ${todo.length} client${todo.length === 1 ? '' : 's'} from their latest nurse assessment?`)) return;
    setDrafting(true); setError('');
    let ok = 0; const failed = [];
    for (const r of todo) { try { await draftPlan(r.id); ok += 1; } catch (e) { failed.push(`${r.client_id}: ${e.message}`); } }
    setDrafting(false);
    if (failed.length) setError(`${ok} drafted. Could not draft ${failed.length}: ${failed.join('; ')}`);
    load();
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows(await getCarePlanBoard()); } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => rows.filter(r => isReceivingService(r)), [rows]);
  const stats = useMemo(() => ({
    clients: active.length,
    none: active.filter(r => r.planStatus.key === 'none').length,
    draft: rows.filter(r => r.draftPlan).length,
    due: active.filter(r => ['due', 'overdue'].includes(r.planStatus.key)).length,
    stale: active.filter(r => r.planStatus.key === 'stale').length,
  }), [rows, active]);

  const filtered = useMemo(() => {
    let l = rows;
    if (filter !== 'all') l = l.filter(r => r.planStatus.key === filter || (filter === 'active' && r.planStatus.key === 'active'));
    if (search) { const q = search.toLowerCase(); l = l.filter(r => `${r.last_name} ${r.first_name} ${r.client_id}`.toLowerCase().includes(q)); }
    return l;
  }, [rows, filter, search]);

  const columns = [
    { id: 'name', header: 'Client', size: 190, meta: { mobile: 'primary' }, accessorFn: r => `${r.last_name}, ${r.first_name}`,
      cell: info => { const r = info.row.original; return <div><div style={{ fontWeight: 500 }}>{r.last_name}, {r.first_name}</div><div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{r.client_id} · {r.current_status}</div></div>; } },
    { id: 'status', header: 'Plan', size: 150, meta: { mobile: 'primary' }, accessorFn: r => r.planStatus.label,
      cell: info => { const s = info.row.original.planStatus; const t = TONE[s.tone]; return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: t.bg, color: t.color, border: `1px solid ${t.border}` }}>{s.label}</span>; } },
    { id: 'version', header: 'Version', size: 80, meta: { mobile: 'hidden', mono: true }, accessorFn: r => r.activePlan?.version_number || r.draftPlan?.version_number || '', cell: info => info.getValue() ? `v${info.getValue()}` : '—' },
    { id: 'effective', header: 'Effective', size: 110, meta: { mobile: 'hidden' }, accessorFn: r => r.activePlan?.effective_date || '', cell: info => fmtD(info.getValue()) },
    { id: 'review', header: 'Review due', size: 110, meta: { mobile: 'secondary' }, accessorFn: r => r.activePlan?.review_due || '', cell: info => fmtD(info.getValue()) },
    { id: 'assessment', header: 'Assessment', size: 110, meta: { mobile: 'hidden' }, accessorFn: r => r.assessment?.assessment_date || '', cell: info => fmtD(info.getValue()) },
    { id: 'tasks', header: 'Tasks', size: 70, meta: { mobile: 'secondary', align: 'right', mono: true }, accessorFn: r => r.taskCount },
    { id: 'skills', header: 'Skills verified', size: 110, meta: { mobile: 'hidden' }, accessorFn: r => r.skills.total ? r.skills.done / r.skills.total : -1,
      cell: info => { const s = info.row.original.skills; return s.total ? <span style={{ color: s.done === s.total ? 'var(--success)' : 'var(--warn)' }}>{s.done} / {s.total}</span> : <span style={{ color: 'var(--text3)' }}>—</span>; } },
    { id: 'signed', header: 'Signed', size: 90, meta: { mobile: 'hidden' }, enableSorting: false,
      cell: info => { const p = info.row.original.activePlan || info.row.original.draftPlan; if (!p) return '—'; return <span style={{ fontSize: 11 }}>{p.rn_signed_at ? 'RN ✓' : 'RN ·'} {p.caregiver_signed_at ? 'CG ✓' : 'CG ·'}</span>; } },
    { id: 'pa', header: 'PA ends', size: 100, meta: { mobile: 'hidden' }, accessorFn: r => r.pa_expiry_date || '', cell: info => fmtD(info.getValue()) },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, marginBottom: 12 }}>
        <StatTile label="Active clients" value={stats.clients} tone="neutral" compact />
        <StatTile label="No care plan" value={stats.none} tone={stats.none ? 'alert' : 'neutral'} compact />
        <StatTile label="Drafts open" value={stats.draft} tone={stats.draft ? 'warn' : 'neutral'} compact />
        <StatTile label="Review due / overdue" value={stats.due} tone={stats.due ? 'warn' : 'neutral'} compact />
        <StatTile label="Newer assessment" value={stats.stale} tone={stats.stale ? 'warn' : 'neutral'} compact />
      </div>
      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{error}</div>}

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title">Care plans &amp; ADLs</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input placeholder="Search client…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', width: 180, fontFamily: 'inherit' }} />
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: filter === k ? 'var(--accent)' : 'transparent', color: filter === k ? 'var(--white)' : 'var(--text3)', borderColor: filter === k ? 'var(--accent)' : 'var(--border)' }}>{l}</button>
            ))}
            <button className="btn btn-outline btn-sm" disabled={drafting} onClick={draftAll} title="Draft a plan for every client with a nurse assessment and no plan">{drafting ? 'Drafting…' : 'Draft all from assessments'}</button>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/settings/tasks')}>Task catalog</button>
          </div>
        </div>
        <DataTable tableId="care-plans" columns={columns} data={filtered} getRowId={r => r.id} loading={loading}
          onRowClick={r => setSelected(r)} emptyMessage="No clients match." maxHeight="calc(100vh - 300px)" />
      </div>

      {selected && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: isMobile ? '100%' : 'min(960px, 80vw)', background: 'var(--bg)', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', zIndex: 200, display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border)' }}>
          <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg2)', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{selected.last_name}, {selected.first_name} <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', fontWeight: 400 }}>{selected.client_id}</span></div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Assessment {fmtD(selected.assessment?.assessment_date)} · PA ends {fmtD(selected.pa_expiry_date)} · {selected.responsible_person_name || '—'}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-outline btn-sm" onClick={() => navigate(`/clients/${selected.id}`)}>Client profile</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>✕</button>
            </div>
          </div>
          <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1 }}>
            <CarePlanEditor client={selected} onChanged={load} />
          </div>
        </div>
      )}
    </div>
  );
}
